import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from "@angular/core";

const AUTOFILL_ANIMATION_NAME = "nexuDetectAutofill";
const KEYFRAMES_STYLE_ID = "nexu-detect-autofill-keyframes";

/**
 * Syncs browser autofill with reactive forms.
 *
 * Chrome/Safari may fill an input on page load without dispatching the `input`
 * event, so Angular's ControlValueAccessor never reads the autofilled value and
 * the FormControl stays empty.
 *
 * This directive detects autofill through the `:-webkit-autofill` CSS animation
 * trick and re-dispatches the event each value accessor actually listens to on
 * desktop, so the form picks up the value:
 *  - plain inputs -> `input` (DefaultValueAccessor)
 *  - currencyMask inputs -> `paste` (ngx-currency ignores `input` on desktop and
 *    reprocesses the field's current text on `paste`)
 *
 * The required keyframes are injected once into the document head, so no global
 * stylesheet setup is needed in the consuming app.
 */
@Directive({
  selector: "input[detectAutofill]",
})
export class DetectAutofillDirective implements OnInit, OnDestroy {
  constructor(private readonly el: ElementRef<HTMLInputElement>, private readonly zone: NgZone) {}

  ngOnInit(): void {
    this.injectKeyframes();
    this.el.nativeElement.addEventListener("animationstart", this.onAnimationStart);
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener("animationstart", this.onAnimationStart);
  }

  private readonly onAnimationStart = (event: AnimationEvent): void => {
    if (event.animationName !== AUTOFILL_ANIMATION_NAME) {
      return;
    }
    // Re-dispatch inside the Angular zone so change detection runs and the
    // value accessor reads the autofilled value. ngx-currency only reprocesses
    // the field on `paste` (its `input` handler is a no-op on desktop), so use
    // `paste` for masked inputs and `input` for everything else.
    const eventType = this.isCurrencyMaskInput() ? "paste" : "input";
    this.zone.run(() => {
      this.el.nativeElement.dispatchEvent(new Event(eventType, { bubbles: true }));
    });
  };

  private isCurrencyMaskInput(): boolean {
    return this.el.nativeElement.hasAttribute("currencyMask");
  }

  private injectKeyframes(): void {
    if (typeof document === "undefined" || document.getElementById(KEYFRAMES_STYLE_ID)) {
      return;
    }
    const style = document.createElement("style");
    style.id = KEYFRAMES_STYLE_ID;
    style.textContent = `
      @keyframes ${AUTOFILL_ANIMATION_NAME} { from { /* noop */ } to { /* noop */ } }
      input[detectAutofill]:-webkit-autofill {
        animation-name: ${AUTOFILL_ANIMATION_NAME};
        animation-duration: 1ms;
      }
    `;
    document.head.appendChild(style);
  }
}
